// Welding bays — eight cards, one per bay, with capacity bars and
// the list of jobs assigned to each. Capacity data comes from
// /api/tools/production-schedule/capacity (active jobs only).
//
// Bay grouping derives from each spool's chosen weld bay (first
// non-skipped weldbayN stage). Legacy jobs with productionJobs.bay
// set but no per-spool weld data fall back to that column so they
// don't vanish during the transition. A job whose spools cross
// multiple bays appears under each of them.
import { useMemo } from "react";
import type {
  BayLoad,
  JobDto,
} from "../../../../../../shared/types/production-schedule";
import { BAYS, BAY_HOURLY_CAPACITY } from "../../../../../../shared/types/production-schedule";
import { StatusBadge } from "../components/StatusBadge";
import { BAY_COLORS } from "../components/BayChip";
import { jobBaysFor } from "../lib/stageHelpers";

interface Props {
  jobs: JobDto[];
  capacity: BayLoad[];
  onOpenJob(id: number): void;
}

export function BaysView({ jobs, capacity, onOpenJob }: Props) {
  const byBay = new Map<number, BayLoad>();
  for (const b of capacity) byBay.set(b.bay, b);
  const jobsByBay = useMemo(() => {
    const map = new Map<number, JobDto[]>();
    for (const j of jobs) {
      for (const b of jobBaysFor({ spools: j.spools, jobBay: j.bay })) {
        const arr = map.get(b) ?? [];
        arr.push(j);
        map.set(b, arr);
      }
    }
    return map;
  }, [jobs]);

  return (
    <div className="bay-cap-grid">
      {BAYS.map((bay) => {
        const list = jobsByBay.get(bay) ?? [];
        const load = byBay.get(bay) ?? { bay, hours: 0, jobCount: 0, utilisationPct: 0 };
        const util = Math.min(100, load.utilisationPct);
        const colour = BAY_COLORS[bay] ?? "#1B449C";
        return (
          <div key={bay} className="bay-cap-card">
            <div className="bay-cap-header" style={{ background: colour }}>
              <div>
                <div className="bay-cap-title">Bay {bay}</div>
                <div className="bay-cap-util">
                  {list.length} job{list.length === 1 ? "" : "s"} · {load.hours.toFixed(1)}h / {BAY_HOURLY_CAPACITY}h
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="bay-cap-title" style={{ fontSize: 18 }}>{util}%</div>
                <div className="bay-cap-util">Utilisation</div>
              </div>
            </div>
            <div
              className="cap-bar-outer"
              style={{ background: colour, margin: "6px 16px 14px" }}
            >
              <div
                className="cap-bar-inner"
                style={{ width: `${util}%`, background: "rgba(255,255,255,0.85)" }}
              />
            </div>
            <div className="bay-jobs-list">
              {list.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 8px", color: "var(--bsp-fg-muted)", fontSize: 12, fontStyle: "italic" }}>
                  Bay available
                </div>
              ) : (
                list.map((j) => (
                  <div
                    key={j.id}
                    className="bay-job-row"
                    onClick={() => onOpenJob(j.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") onOpenJob(j.id);
                    }}
                  >
                    <span className="proj-id">{j.jobNumber}</span>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: "var(--bsp-dark)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {j.name}
                    </div>
                    <StatusBadge status={j.status} />
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
